import FeatureGates, {
  type GetExperimentValueOptions,
} from '@atlassian/feature-gate-js-client';

/**
 * When creating an experiment, we configure params for that experiment to drive
 * behaviour in code (rather than using hard-coded cohort names)
 *
 * With A/B testing, we can use a simple boolean param (`enabled`) to control
 * behaviour - note that this param has to be configured when you create a new
 * experiment in Statsig.
 *
 * See: https://docs.statsig.com/experiments-plus/create-new#configure-your-groups-and-parameters
 */
export const experimentConfig = {
  /**
   * A/A test for validating our Statsig integration and our metrics instrumentation
   * https://switcheroo.atlassian.com/gates/5f23451e-6414-449b-a04c-e917a07fb929
   */
  whiteboardAATest: {
    experimentId: 'whiteboards-aa-test',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * An experiment to show the dotted grid as the default option in a whiteboard
   * https://switcheroo.atlassian.com/ui/gates/b8221421-a6df-4aaa-98d1-31f85b7d84d5
   */
  dottedGridDefault: {
    experimentId: 'cc-whiteboards-dotted-grid',
    defaultParams: {
      enabled: false,
      isDefaultDottedGrid: false,
    },
  },
  /**
   * An experiment to show follow cursor in whiteboards
   * https://switcheroo.atlassian.com/ui/gates/6bea026f-1a83-4f81-97ac-11d1588966cc
   */
  followCursor: {
    experimentId: 'confluence_whiteboards_follow_cursor_experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * An experiment to show AI generate stickies feature in whiteboards
   * https://switcheroo.atlassian.com/ui/gates/e006c8f6-79f9-44b7-ac13-615d3523e1d6
   */
  aiGenerate: {
    experimentId: 'whiteboards_ai_generate_stickies',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * An experiment to show AI clustering feature in whiteboards
   * @see {@link https://switcheroo.atlassian.com/ui/gates/1f3c8245-5823-4f3e-a3ce-6246704234ef/key/whiteboards_ai_clustering_real}
   */
  aiClustering: {
    experimentId: 'whiteboards_ai_clustering_real',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * An experiment that needs the template picker to be opened on an empty board
   * https://switcheroo.atlassian.com/ui/gates/6ebf127b-c1e6-48ba-8cd0-355fb0ba5825
   *
   */
  cfind2065NBPSExperience: {
    experimentId: 'cfind-2065-nbps-in-product-experience',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * An experiment that needs the template picker to be opened on an empty board
   * https://switcheroo.atlassian.com/ui/gates/0f521eb8-13b3-434b-aa96-387acd2b26d3
   */
  cfind2079SampleContent: {
    experimentId: 'cfind-2079_create_sample_content',
    defaultParams: {
      enabled: false,
    },
  },
} satisfies Record<string, Experiment>;

type Experiment = {
  experimentId: string;
  defaultParams: Record<string, any>;
};

type ExperimentConfig = typeof experimentConfig;
type ExperimentName = keyof ExperimentConfig;
type ExperimentParams = {
  [key in keyof ExperimentConfig]: ExperimentConfig[key]['defaultParams'];
};

/**
 * Defining our own type so we can mark `fireExperimentExposure` as required to
 * enforce explicit opt-in of firing exposure events.
 */
type ExperimentValueOptions<T> = {
  fireExperimentExposure: boolean;
} & GetExperimentValueOptions<T>;

/**
 * Returns the value of a given parameter for the provided experiment
 *
 * **NOTE**: You must explicitly set whether you want this call to fire an
 * exposure event or not. This is done with the `options` param (see {@link ExperimentValueOptions}).
 *
 * If you're unsure whether you should be firing an exposure event, reach out to
 * the team experimentation champions.
 *
 * This page explains the importance of exposure events: {@link https://hello.atlassian.net/wiki/spaces/~732385844/pages/3187295823/Exposure+Events+101}
 *
 * @example getExperimentValue('whiteboardAATest', 'enabled', { fireExperimentExposure: true })
 * @example getExperimentValue('anotherExperiment', 'btnColor', { fireExperimentExposure: false })
 */
export const getExperimentValue = <
  ExpName extends ExperimentName,
  ParamName extends keyof ExperimentParams[ExpName],
  ParamType extends ExperimentParams[ExpName][ParamName]
>(
  experimentName: ExpName,
  paramName: ParamName,
  /**
   * Statsig options for fetching the experiment value. `fireExposureEvent` is required
   */
  options: ExperimentValueOptions<ParamType>
): ParamType => {
  const { experimentId, defaultParams } = experimentConfig[experimentName];

  // Typescript is unable to discriminate between experiment params
  // This should be okay to cast as TypeScript will throw an error if we try to call this
  // function with a param name that doesn't exist for the provided experiment
  const defaultParam = (defaultParams as any)[paramName] as ParamType;

  return FeatureGates.getExperimentValue(
    experimentId,
    paramName as string,
    defaultParam,
    options
  );
};

/**
 * Tells Statsig to manually log an exposure event, useful when you've already
 * evaluated an experiment but explicitly told it not to fire an experiment exposure
 *
 * Note that this is idempotent
 *
 * See: https://docs.statsig.com/client/jsClientSDK#manual-exposures-
 */
export const manuallyFireExposureEvent = (experimentName: ExperimentName) => {
  const experimentId = experimentConfig[experimentName].experimentId;

  return FeatureGates.manuallyLogExperimentExposure(experimentId);
};
